import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, withScopeId as _withScopeId, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-489ff850")
const _hoisted_1 = { class: "label" }
const _hoisted_2 = ["type", "name", "value", "disabled"]
const _hoisted_3 = {
  key: 2,
  class: /*#__PURE__*/_normalizeClass(['error'])
}
_popScopeId()

import { useAttrs, computed } from 'vue';
import { HtmlInputEvent } from '@/types';
import useInputStyles from './composables/useInputStyles';
import useInputEvents from './composables/useInputEvents';
import useInputValidation from './composables/useInputValidation';
import { omit } from 'lodash';

/**
 * TYPES
 */
type InputValue = string | number;
type InputType = 'text' | 'number' | 'date' | 'email' | 'password';
type InputSize = 'xs' | 'sm' | 'md' | 'lg';
type ValidationTrigger = 'input' | 'blur';
type RuleFunction = (val: InputValue) => string;
export type Rules = RuleFunction[];

type Props = {
  name: string;
  modelValue: InputValue;
  isValid?: boolean;
  type?: InputType;
  size?: InputSize;
  disabled?: boolean;
  label?: string;
  inputAlignRight?: boolean;
  decimalLimit?: number;
  validateOn?: ValidationTrigger;
  rules?: Rules;
  noRadius?: boolean;
  noShadow?: boolean;
  noBorder?: boolean;
  format?: (input: string | number) => string | number;
  containerBg?: string;
  changeType?: string;
};

/**
 * PROPS & EMITS
 */

// https://v3.vuejs.org/api/sfc-script-setup.html#usage-alongside-normal-script
const __default__ = {
  inheritAttrs: false
};

function setup(__props: {
  name: string;
  modelValue: InputValue;
  isValid?: boolean;
  type?: InputType;
  size?: InputSize;
  disabled?: boolean;
  label?: string;
  inputAlignRight?: boolean;
  decimalLimit?: number;
  validateOn?: ValidationTrigger;
  rules?: Rules;
  noRadius?: boolean;
  noShadow?: boolean;
  noBorder?: boolean;
  format?: (input: string | number) => string | number;
  containerBg?: string;
  changeType?: string;
}, { emit }: { emit: ({
  (e: 'blur', value: string): void;
  (e: 'input', value: string): void;
  (e: 'update:modelValue', value: string): void;
  (e: 'update:isValid', value: boolean): void;
  (e: 'keydown', value: HtmlInputEvent);
}), expose: any, slots: any, attrs: any }) {

const props = __props



/**
 * COMPOSABLES
 */
const attrs = useAttrs();
const { errors, isInvalid, validate } = useInputValidation(props, emit);
const {
  parentClasses,
  inputContainerClasses,
  inputGroupClasses,
  headerClasses,
  footerClasses,
  inputClasses,
  prependClasses,
  appendClasses,
  borderRadiusClasses
} = useInputStyles(props, isInvalid, attrs);
const { onInput, onKeydown, onBlur } = useInputEvents(props, emit, validate);
const borderRadiusCompCls = computed(() => {
  if (props.changeType === 'trade') {
    return {
      rounded: true
    };
  } else if (props.changeType === 'createPool') {
    return {
      rounded: true,
      'dark:border-gray-1050': !props.noBorder
    };
  } else if (props.changeType === 'invest') {
    return {
      rounded: true,
      'dark:border-gray-1050': !props.noBorder
    };
  } else if (props.changeType === 'stake') {
    return {
      rounded: true,
      'dark:border-gray-1050': !props.noBorder
    };
  }
  return borderRadiusClasses;
});

const reverseCls = computed(() => {
  if (props.changeType === 'stake') {
    return 'flex flex-row-reverse';
  }
  return '';
});

const parentCompCls = computed(() => {
  if (props.changeType === 'trade') {
    return {
      'shadow-none': true
    };
  }
  return parentClasses;
});

/**
 * COMPUTED
 */

// We don't want to pass on parent level classes to the html
// input element. So we need to remove it from the attrs object.
const inputAttrs = computed(() => omit(attrs, 'class'));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['bal-text-input', _unref(parentCompCls), _unref(borderRadiusCompCls)])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass([
        __props.containerBg ? __props.containerBg : 'input-container',
        _unref(inputContainerClasses),
        _unref(borderRadiusCompCls)
      ])
    }, [
      (_ctx.$slots.header || __props.label)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(['header', _unref(headerClasses)])
          }, [
            _renderSlot(_ctx.$slots, "header", {}, () => [
              _createElementVNode("span", _hoisted_1, _toDisplayString(__props.label), 1)
            ])
          ], 2))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(['input-group', _unref(inputGroupClasses), _unref(reverseCls)])
      }, [
        (_ctx.$slots.prepend)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(['prepend', _unref(prependClasses)])
            }, [
              _renderSlot(_ctx.$slots, "prepend")
            ], 2))
          : _createCommentVNode("", true),
        _createElementVNode("input", _mergeProps({
          type: __props.type,
          name: __props.name,
          value: __props.modelValue
        }, _unref(inputAttrs), {
          disabled: __props.disabled,
          onBlur: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onBlur) && _unref(onBlur)(...args))),
          onInput: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onInput) && _unref(onInput)(...args))),
          onKeydown: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(onKeydown) && _unref(onKeydown)(...args))),
          class: [
            'input',
            _unref(inputClasses),
            __props.changeType ? 'inp-caret' : '',
            __props.changeType ? 'placeholder-custom-black-350' : ''
          ]
        }), null, 16, _hoisted_2),
        (_ctx.$slots.append)
          ? (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: _normalizeClass(['append', _unref(appendClasses)])
            }, [
              _renderSlot(_ctx.$slots, "append")
            ], 2))
          : _createCommentVNode("", true)
      ], 2),
      (_ctx.$slots.footer)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass(['footer', _unref(footerClasses)])
          }, [
            _renderSlot(_ctx.$slots, "footer")
          ], 2))
        : _createCommentVNode("", true),
      (_unref(isInvalid))
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(errors)[0]), 1))
        : _createCommentVNode("", true)
    ], 2)
  ], 2))
}
}


export default _defineComponent({
  ...__default__,
  props: {
    name: { type: String, required: true },
    modelValue: { type: [String, Number], required: true, default: '' },
    isValid: { type: Boolean, required: false, default: true },
    type: { type: String, required: false, default: 'text' },
    size: { type: String, required: false, default: 'md' },
    disabled: { type: Boolean, required: false, default: false },
    label: { type: String, required: false },
    inputAlignRight: { type: Boolean, required: false, default: false },
    decimalLimit: { type: Number, required: false, default: 18 },
    validateOn: { type: String, required: false, default: 'blur' },
    rules: { type: Array, required: false, default: () => [] },
    noRadius: { type: Boolean, required: false, default: false },
    noShadow: { type: Boolean, required: false, default: false },
    noBorder: { type: Boolean, required: false, default: false },
    format: { type: Function, required: false },
    containerBg: { type: String, required: false, default: '' },
    changeType: { type: String, required: false, default: '' }
  } as unknown as undefined,
  emits: ["blur", "input", "update:modelValue", "update:isValid", "keydown"] as unknown as undefined,
  setup})